import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Categories.css';

// Oscar statuette SVG icon
const OscarIcon = () => (
  <svg className="oscar-svg" viewBox="0 0 24 40" fill="currentColor">
    <ellipse cx="12" cy="3.5" rx="3" ry="3.5"/>
    <path d="M12 7 L12 12 M8 10 L16 10" strokeWidth="1.5" stroke="currentColor" strokeLinecap="round"/>
    <path d="M7 12 Q6 18 8 24 L10 24 L10 32 L6 32 L6 36 L18 36 L18 32 L14 32 L14 24 L16 24 Q18 18 17 12 Z"/>
    <rect x="5" y="36" width="14" height="3" rx="1"/>
  </svg>
);

// Get tier info
const getTierInfo = (tier) => {
  const tierNum = parseInt(tier);
  switch(tierNum) {
    case 1: return { name: 'Tier 1', color: 'gold', points: '1 pt' };
    case 2: return { name: 'Tier 2', color: 'silver', points: '2 pts' };
    case 3: return { name: 'Tier 3', color: 'bronze', points: '3 pts' };
    default: return { name: 'Tier 1', color: 'gold', points: '1 pt' };
  }
};

function Categories() {
  const { selectedGroup, setSelectedGroup } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [deadline, setDeadline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('all');
  const [groupLoading, setGroupLoading] = useState(!selectedGroup);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // If no selectedGroup, try to load user's groups
    if (!selectedGroup) {
      loadGroups();
    }
  }, []);

  useEffect(() => {
    if (!groupLoading) {
      loadData();
    }
  }, [selectedGroup, groupLoading]);

  const loadGroups = async () => {
    try {
      const groups = await api.getMyGroups();
      if (groups.length > 0) {
        setSelectedGroup(groups[0]);
      }
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setGroupLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [categoriesData, deadlineData] = await Promise.all([
        api.getCategories(),
        api.getDeadline()
      ]);

      setCategories(categoriesData);
      setDeadline(deadlineData);

      if (selectedGroup) {
        const myPredictions = await api.getMyPredictions(selectedGroup._id);
        setPredictions(myPredictions.predictions || {});
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = selectedTier === 'all'
    ? categories
    : categories.filter(c => c.tier === parseInt(selectedTier));

  const isPastDeadline = deadline?.isPastDeadline;
  const completedCount = Object.keys(predictions).length;

  const handleCopySelections = () => {
    const lines = ['My Oscar Predictions', ''];
    categories.forEach(cat => {
      const pick = predictions[cat.name];
      if (pick) lines.push(`${cat.name}: ${pick}`);
    });
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tierCounts = {
    all: categories.length,
    '1': categories.filter(c => c.tier === 1).length,
    '2': categories.filter(c => c.tier === 2).length,
    '3': categories.filter(c => c.tier === 3).length,
  };

  if (loading || groupLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!selectedGroup) {
    return (
      <div className="categories-page">
        <div className="card">
          <p>Please join or create a group first to make predictions.</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="page-header-row">
        <div className="title-section">
          <h1>Categories</h1>
          <span className="completed-indicator">
            {completedCount}/{categories.length} completed
            {isPastDeadline && <span className="deadline-passed"> - Voting closed</span>}
          </span>
        </div>
        <div className="custom-select filter-dropdown">
          <select
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
          >
            <option value="all">All ({tierCounts.all})</option>
            <option value="1">Tier 1 ({tierCounts['1']})</option>
            <option value="2">Tier 2 ({tierCounts['2']})</option>
            <option value="3">Tier 3 ({tierCounts['3']})</option>
          </select>
        </div>
      </div>

      {isPastDeadline && (
        <div className="deadline-banner">
          Voting has closed. You can no longer change your predictions.
        </div>
      )}

      {/* Progress Bar */}
      <div className="progress-section">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(completedCount / categories.length) * 100}%` }}
          ></div>
        </div>
        <span className="progress-text">{Math.round((completedCount / categories.length) * 100)}%</span>
      </div>

      {completedCount === categories.length && categories.length > 0 && (
        <button className="btn btn-copy-selections" onClick={handleCopySelections}>
          {copied ? 'Copied!' : 'Copy My Selections'}
        </button>
      )}

      {/* Categories Grid */}
      <div className="categories-grid">
        {filteredCategories.map(category => {
          const hasPrediction = predictions[category.name];
          const tierInfo = getTierInfo(category.tier);

          return (
            <button
              key={category._id}
              className={`category-card ${hasPrediction ? 'completed' : ''}`}
              onClick={() => navigate(`/categories/${encodeURIComponent(category.name)}`)}
            >
              <div className="oscar-icon-wrapper">
                <OscarIcon />
              </div>
              <span className="category-name">{category.name}</span>
              <span className={`tier-label ${tierInfo.color}`}>
                {tierInfo.name} - {tierInfo.points}
              </span>
              {hasPrediction && (
                <span className="category-check">&#10003;</span>
              )}
            </button>
          );
        })}
      </div>

      {filteredCategories.length === 0 && (
        <div className="no-categories">
          No categories found for the selected tier.
        </div>
      )}
    </div>
  );
}

export default Categories;
